import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Tenant, UserRole } from '@/types';

interface AuthState {
  /** JWT 访问令牌 */
  token: string | null;
  /** 刷新令牌 */
  refreshToken: string | null;
  /** 当前用户 */
  user: User | null;
  /** 当前租户 */
  currentTenant: Tenant | null;
  /** 当前角色 */
  currentRole: UserRole | null;
  /** 是否咨询师（跨租户只读身份，仅 active+认证通过 为 true） */
  isConsultant: boolean;
  /** 咨询师审核状态：active/pending/disabled/null（非咨询师为 null） */
  consultantStatus: 'active' | 'pending' | 'disabled' | null;
  /** KYC 认证状态：approved/pending/rejected/null（认证通过且账号 active 才视为有效咨询师） */
  certStatus: 'approved' | 'pending' | 'rejected' | null;
  /** 是否平台管理员（任一租户持有 platform_admin 即为 true，跨租户全局有效） */
  isPlatformAdmin: boolean;
  /** 是否授权渠道（平台管理员显式授权，可查看自己直接下线的利润自测线索） */
  isChannel: boolean;
  /** 用户身份（注册/改身份选择的 owner/executive/consultant/other），可空 */
  identity: string | null;
  /** 设置 Token */
  setToken: (token: string, refreshToken?: string) => void;
  /** 设置咨询师标识 */
  setConsultant: (isConsultant: boolean) => void;
  /** 设置咨询师审核状态 */
  setConsultantStatus: (status: 'active' | 'pending' | 'disabled' | null) => void;
  /** 设置 KYC 认证状态 */
  setCertStatus: (status: 'approved' | 'pending' | 'rejected' | null) => void;
  /** 设置平台管理员标志 */
  setIsPlatformAdmin: (isPlatformAdmin: boolean) => void;
  /** 设置授权渠道标志 */
  setIsChannel: (isChannel: boolean) => void;
  /** 设置用户身份 */
  setIdentity: (identity: string | null) => void;
  /** 设置用户信息 */
  setUser: (user: User) => void;
  /** 设置当前租户 */
  setCurrentTenant: (tenant: Tenant, role: UserRole) => void;
  /** 清除认证信息 */
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      currentTenant: null,
      currentRole: null,
      isConsultant: false,
      consultantStatus: null,
      certStatus: null,
      isPlatformAdmin: false,
      isChannel: false,
      identity: null,
      setToken: (token, refreshToken) =>
        set((state) => ({
          token,
          refreshToken: refreshToken ?? state.refreshToken,
        })),
      setUser: (user) => set({ user }),
      setCurrentTenant: (currentTenant, currentRole) =>
        set({ currentTenant, currentRole }),
      setConsultant: (isConsultant) => set({ isConsultant }),
      setConsultantStatus: (consultantStatus) => set({ consultantStatus }),
      setCertStatus: (certStatus) => set({ certStatus }),
      setIsPlatformAdmin: (isPlatformAdmin) => set({ isPlatformAdmin }),
      setIsChannel: (isChannel) => set({ isChannel }),
      setIdentity: (identity) => set({ identity }),
      clearAuth: () =>
        set({
          token: null,
          refreshToken: null,
          user: null,
          currentTenant: null,
          currentRole: null,
          isConsultant: false,
          consultantStatus: null,
          certStatus: null,
          isPlatformAdmin: false,
          isChannel: false,
          identity: null,
        }),
    }),
    {
      name: 'ibusiness-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        currentTenant: state.currentTenant,
        currentRole: state.currentRole,
        isConsultant: state.isConsultant,
        consultantStatus: state.consultantStatus,
        certStatus: state.certStatus,
        isPlatformAdmin: state.isPlatformAdmin,
        isChannel: state.isChannel,
        identity: state.identity,
      }),
    }
  )
);
